import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { utilService } from '../../services/util.service'
import { fetchPosts } from '../../store/slices/post.slice'
import greyBlock from '../../assets/imgs/Home/HeroAnn/greyBlock.png'
import redBlock from '../../assets/imgs/Home/HeroAnn/redBlock.png'
import { Button } from '../ui/elements'

export function HeroAnnouncements() {
  const dispatch = useDispatch()
  const [announcementPosts, setAnnouncementPosts] = useState([])
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (announcementPosts.length) return
    dispatch(fetchPosts({ tags: 'הכרזה', limit: 3 }))
      .unwrap()
      .then((posts) =>
        setAnnouncementPosts(
          [...posts].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
        )
      )
      .catch((err) => console.error('❌ שגיאה בטעינת הכרזות:', err))
  }, [dispatch, announcementPosts.length])

  useEffect(() => {
    if (!announcementPosts.length || intervalRef.current) return

    intervalRef.current = setInterval(() => {
      setActiveSlideIndex(
        (prevIndex) => (prevIndex + 1) % announcementPosts.length
      )
    }, 5000)

    return () => clearInterval(intervalRef.current)
  }, [announcementPosts.length])

  const handleSlideClick = (index) => {
    setActiveSlideIndex(index)
    resetInterval()
  }

  const resetInterval = () => {
    clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      setActiveSlideIndex(
        (prevIndex) => (prevIndex + 1) % announcementPosts.length
      )
    }, 5000)
  }

  if (!announcementPosts.length) {
    return <p>אין כרזות להצגה.</p>
  }

  return (
    <section
      className="overflow-hidden md:p-4 md:mt-20 grid gap-6 md:grid-cols-[30%_70%] lg:grid-cols-[20%_40%_40%]"
      aria-label="רכיב הכרזות הראשי באתר"
    >
      <div
        className="order-2 md:order-1 flex md:flex-col gap-2 overflow-x-auto md:overflow-hidden flex-nowrap"
        aria-label="ניווט בין כרזות"
      >
        {announcementPosts.map((post, index) => (
          <div
            key={post._id}
            className="relative flex-shrink-0 lg:w-[200px] group"
            aria-current={index === activeSlideIndex}
          >
            <button
              onClick={() => handleSlideClick(index)}
              className="w-full h-[95%]"
              aria-label={`עבור לכרזה מספר ${index + 1}: ${post.title}`}
              title={`צפייה בכרזה ${post.title}`}
            >
              <img
                src={index === activeSlideIndex ? redBlock : greyBlock}
                alt={
                  index === activeSlideIndex
                    ? `כרזה פעילה מספר ${index + 1}`
                    : `כרזה מספר ${index + 1}`
                }
                title={`כפתור כרזה מספר ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-start justify-start pr-5 pt-1">
                <span className="text-2xl" aria-hidden="true">
                  0{index + 1}
                </span>
              </div>
              <div className="absolute inset-0 flex items-end justify-start p-7">
                <h2
                  className="text-left text-lg"
                  aria-label={`כותרת הכרזה: ${post.title}`}
                >
                  {post.title}
                </h2>
              </div>
            </button>
          </div>
        ))}
      </div>

      <div className="order-1 md:order-2 flex flex-col gap-6 lg:justify-center">
        <div className="relative aspect-[4/3] lg:hidden" aria-hidden="true">
          {announcementPosts.map((post, index) => (
            <img
              key={post._id}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                index === activeSlideIndex
                  ? 'opacity-100 z-10'
                  : 'opacity-0 z-0'
              }`}
              src={post.imageUrl}
              alt={`תמונה של הכרזה: ${post.title}`}
              title={`תמונה של הכרזה: ${post.title}`}
            />
          ))}
        </div>

        <div
          className="md:text-right md:grid md:grid-cols-[55%_45%] lg:flex lg:flex-col lg:gap-6"
          aria-label="פרטי הכרזה הנוכחית"
        >
          <div>
            <p className="text-muted mb-2" aria-label="כותרת משנית">
              {announcementPosts[activeSlideIndex]?.title || ''}
            </p>
            <h1
              className="text-4xl lg:text-5xl max-w-[80%]"
              aria-label="תיאור קצר של הכרזה"
              title={announcementPosts[activeSlideIndex]?.description}
            >
              {utilService.truncateText(
                announcementPosts[activeSlideIndex]?.description || '',
                80
              )}
            </h1>
          </div>

          <Button
            to={`/post/${announcementPosts[activeSlideIndex]?._id}`}
            text="קרא עוד"
            width={150}
            height={60}
            fontSize="text-lg"
            textColor="text-accentLight"
            bgColor="fill-buttonSecondary1"
            hoverBgColor="hover:scale-105 transform duration-300"
            className="md:self-end lg:self-start my-6"
            aria-label={`מעבר לעמוד הכרזה: ${announcementPosts[activeSlideIndex]?.title}`}
            title={`קרא עוד על ${announcementPosts[activeSlideIndex]?.title}`}
          />
        </div>
      </div>

      <div
        className="hidden lg:block self-center relative aspect-[4/3] order-3"
        aria-hidden="true"
      >
        {announcementPosts.map((post, index) => (
          <img
            key={post._id}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === activeSlideIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
            src={post.imageUrl}
            alt={`תמונה של הכרזה: ${post.title}`}
            title={`תמונה של הכרזה: ${post.title}`}
          />
        ))}
      </div>
    </section>
  )
}
